<template lang="pug">
v-card.tenant-pipeline
  v-card-title.d-flex.justify-center(style='color:#222222; font-weight: 700; font-size: 20px;') {{$t('TENANT_PIPELINE.TITLE')}}
  v-stepper.elevation-0(:value='step' alt-labels)
    v-stepper-header.elevation-0
      template(v-for='(item, index) in stages')
        v-stepper-step(:key='`step-${index}`' :step='index + 1' :complete='step > index + 1' color='#18bedf')
          span.text-center(style='font-size: 13px;') {{ item }}
        v-divider(v-if='index < stages.length - 1' :key='`divider-${index}`')
  v-card-text.pipeline-body
    .mb-3(v-if='tenant' style='color:#444444; font-weight: 500; font-size: 14px;')
      span {{$t('TENANT_PIPELINE.CURRENT_TENANT')}}：
      span.font-weight-bold {{ tenant.schema_name }}
      span.ml-2(v-if='tenant.description && tenant.description !== tenant.schema_name' style='color:#888888;') ({{ tenant.description }})
    v-alert.mb-3(v-if='errorMessage' type='error' dense outlined dismissible @input='errorMessage = ""')
      div(style='white-space: pre-wrap;') {{ errorMessage }}
    .position-relative
      tenant-step(v-if='step === 1' ref='stage' :tenantListItems='tenantListItems' :busy='busy' @status='onStatus' @progress='onProgress')
      admin-step(v-if='step === 2' ref='stage' :tenant='tenant' :busy='busy' @status='onStatus' @progress='onProgress')
      upchat-step(v-if='step === 3' ref='stage' :tenant='tenant' :busy='busy' @status='onStatus' @progress='onProgress')
      search-step(v-if='step === 4' ref='stage' :tenant='tenant' :busy='busy' @status='onStatus' @progress='onProgress')
      work-agent-step(v-if='step === 5' ref='stage' :tenant='tenant' :busy='busy' @status='onStatus' @progress='onProgress')
      upagent-step(v-if='step === 6' ref='stage' :tenant='tenant' :busy='busy' @status='onStatus' @progress='onProgress')
      v-overlay(:value='busy' absolute color='#ffffff' opacity='0.8')
        .d-flex.flex-column.align-center
          v-progress-circular(indeterminate color='#18bedf' size='36')
          .mt-3(style='color:#444444; font-size: 14px;') {{ progressMessage }}
  div.d-flex.align-center.justify-end.pipeline-actions
    v-btn.mr-4(:ripple='false' @click='onCancel' width='100' height='40' color='cancel' depressed outlined style='font-weight: 700;' :disabled='busy') {{$t('GENERAL.CANCEL')}}
    v-btn.mr-4(v-if='step > 1' :ripple='false' @click='onPrevious' width='100' height='40' color='cancel' depressed outlined style='font-weight: 700;' :disabled='busy') {{$t('TENANT_PIPELINE.PREVIOUS')}}
    v-btn.mr-6(:ripple='false' @click='onPrimary' width='100' height='40' color='#14C6DE' depressed :dark='!primaryDisabled' :disabled='primaryDisabled')
      span(style='font-weight: 700;') {{ primaryLabel }}
</template>

<script>
import Vue from "vue";
import i18n from '/common/plugins/vue-i18n.js';
import tenantStep from "@/components/TenantCenter/Pipeline/pipelineTenantStep";
import adminStep from "@/components/TenantCenter/Pipeline/pipelineAdminStep";
import upchatStep from "@/components/TenantCenter/Pipeline/pipelineUpChatStep";
import searchStep from "@/components/TenantCenter/Pipeline/pipelineSearchStep";
import workAgentStep from "@/components/TenantCenter/Pipeline/pipelineWorkAgentStep";
import upagentStep from "@/components/TenantCenter/Pipeline/pipelineUpAgentStep";

// 以流程設定租戶：六個階段的外框。
// 各階段元件的契約：
//   emit('status', { dirty, ready })  dirty＝有待寫入的異動；ready＝目前可以送出／前進
//   emit('progress', message)         送出期間要顯示的進行中訊息（可多次更新）
//   submit()                          寫入異動；失敗時 throw Error(訊息)。
//                                     第一階段回傳選定／新建的租戶物件
// 寫入成功後自動前進到下一階段；最後一階段則停留，由使用者按「完成」關閉精靈。
// 部分失敗（submit throw）時停留在原階段顯示錯誤，供使用者修正後重試。
export default Vue.extend({
  props: {
    tenantListItems: {
      type: Array,
      require: true,
    },
  },
  components: {
    tenantStep,
    adminStep,
    upchatStep,
    searchStep,
    workAgentStep,
    upagentStep,
  },
  data() {
    return {
      step: 1,
      tenant: null,
      status: { dirty: false, ready: false },
      busy: false,
      progressMessage: '',
      errorMessage: '',
      stages: [
        i18n.t('TENANT_PIPELINE.STAGE_TENANT'),
        i18n.t('TENANT_PIPELINE.STAGE_ADMIN'),
        i18n.t('TENANT_PIPELINE.STAGE_UPCHAT'),
        i18n.t('TENANT_PIPELINE.STAGE_PORTAL'),
        i18n.t('TENANT_PIPELINE.STAGE_WORK_AGENT'),
        i18n.t('TENANT_PIPELINE.STAGE_UPAGENT'),
      ],
    };
  },
  computed: {
    isLastStep() {
      return this.step === this.stages.length;
    },
    primaryLabel() {
      if (this.status.dirty) {
        return this.step <= 2 ? i18n.t('TENANT_PIPELINE.CREATE') : i18n.t('TENANT_PIPELINE.UPDATE');
      }
      return this.isLastStep ? i18n.t('TENANT_PIPELINE.FINISH') : i18n.t('TENANT_PIPELINE.NEXT');
    },
    primaryDisabled() {
      return this.busy || !this.status.ready;
    },
  },
  methods: {
    onStatus(val) {
      this.status = val;
    },
    onProgress(message) {
      this.progressMessage = message;
    },
    goTo(step) {
      this.errorMessage = '';
      this.status = { dirty: false, ready: false };
      this.step = step;
    },
    onPrevious() {
      this.goTo(this.step - 1);
    },
    async onPrimary() {
      if (!this.status.dirty) {
        if (this.step === 1) {
          this.tenant = this.$refs.stage.selectedTenant();
        }
        if (this.isLastStep) {
          this.$emit('emitTenantPipelineDialog', { refresh: true });
        } else {
          this.goTo(this.step + 1);
        }
        return;
      }
      this.busy = true;
      this.errorMessage = '';
      this.progressMessage = '';
      try {
        const result = await this.$refs.stage.submit();
        if (this.step === 1) {
          this.tenant = result;
        }
        // 最後一階段寫入後停留，讓使用者看到新產生的啟用碼；按「完成」才關閉
        if (!this.isLastStep) {
          this.goTo(this.step + 1);
        }
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.busy = false;
      }
    },
    onCancel() {
      // 已經「建立」或「更新」完成的階段不會還原
      this.$emit('emitTenantPipelineDialog', { refresh: this.tenant !== null });
    },
  },
});
</script>

<style lang="scss" scoped>
.tenant-pipeline {
  .pipeline-body {
    min-height: 360px;
    max-height: 62vh;
    overflow-y: auto;
  }
  .pipeline-actions {
    background-color: #f4f4f4;
    height: 60px;
  }
}
</style>
